import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { loadPronunciationGuideCatalog } from '../../services/dataApi';

// The target language's own pronunciation-guide lesson catalog — mirrors
// the mobile app's PronunciationGuideScreen: just a chapter picker, each
// chapter opening as its own interactive lesson via LessonFlow.
export default function PronunciationGuidePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const targetCode = languagePrefs?.targetLanguage || '';

  const [chapters, setChapters] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!targetCode) return;
    let active = true;
    setChapters(null);
    setError(false);
    loadPronunciationGuideCatalog(targetCode)
      .then((loaded) => { if (active) setChapters(loaded); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [targetCode]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
        >
          ← {t('nav.home')}
        </button>

        <h1 className="text-2xl font-black text-slate-900">{t('pronunciationGuide.title', 'Pronunciation Guide')}</h1>
        <p className="mt-1.5 text-sm text-slate-500">
          {t('pronunciationGuide.desc', 'Learn how each sound is made — mouth position, airflow, and practice words.')}
        </p>

        <div className="mt-6">
          {error || (chapters && chapters.length === 0) ? (
            <p className="py-10 text-center text-sm text-slate-500">{t('learningPaths.topicUnavailable')}</p>
          ) : !chapters ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-200/70" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {chapters.length > 1 && (
                <p className="mb-1 text-xs font-black uppercase tracking-wider text-slate-400">
                  {t('journeyPages.startInteractiveLesson')}
                </p>
              )}
              {chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => navigate(`/learn/pronunciation-guide/${chapter.id}/lesson`)}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-left shadow-sm transition hover:border-violet-300 hover:shadow-md"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-violet-100 text-sm font-black text-violet-700">
                    {chapter.chapterNumber}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{chapter.label}</span>
                  <span className="shrink-0 text-slate-300" aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
